/* nw-header-nn: behaviour for sections/nw-header-nn.liquid (Nothing Naughty / Empire-style header).
   - sticky: header goes position:fixed once the utility bar has scrolled away; .nnh-holder keeps the flow
     height so the page never jumps. Desktop collapses to the main row after a further 68px (Empire does this),
     and the hamburger then re-opens the category row.
   - mobile: hamburger opens a <dialog> drawer with accordions; the search icon swaps the row for a search field.
   - live search: /search/suggest?section_id=nw-nnh-search-results (server-rendered, market-correct prices).
   Elements carry data-skip-node-update / data-skip-subtree-update so Horizon's cart morph (which re-renders
   this section on every cart change) leaves our JS state alone. */

const DESKTOP = window.matchMedia('(min-width: 1024px)');
const COLLAPSE_AFTER = 68; // desktop: px scrolled past the stick point before the category row folds away
const RESULTS_SECTION = 'nw-nnh-search-results';

function initHeader(root) {
  const header = root.querySelector('.nnh-header');
  const utility = root.querySelector('.nnh-utility');
  const toggle = root.querySelector('[data-nnh-toggle]');
  const drawer = root.querySelector('[data-nnh-drawer]');
  const search = root.querySelector('[data-nnh-search]');
  const input = root.querySelector('[data-nnh-input]');
  const fly = root.querySelector('[data-nnh-fly]');
  const results = root.querySelector('[data-nnh-results]');

  /* ---------- sticky / collapse ---------- */
  let ticking = false;
  const setVars = () => {
    const h = header.offsetHeight;
    document.body.style.setProperty('--header-height', `${h}px`);
    const uh = utility && utility.offsetHeight ? utility.offsetHeight : 0;
    document.body.style.setProperty('--header-group-height', `${root.hasAttribute('data-stuck') ? h : h + uh}px`);
  };
  const update = () => {
    ticking = false;
    const y = window.scrollY;
    const uh = utility && utility.offsetHeight ? utility.offsetHeight : 0;
    const stuck = y > uh;
    const collapsed = DESKTOP.matches && y > uh + COLLAPSE_AFTER;
    root.toggleAttribute('data-stuck', stuck);
    if (root.hasAttribute('data-collapsed') !== collapsed) {
      root.toggleAttribute('data-collapsed', collapsed);
      if (!collapsed) setNavOpen(false);
    }
    setVars();
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* ---------- hamburger: desktop (collapsed) = category row, mobile = drawer ---------- */
  function setNavOpen(open) {
    root.toggleAttribute('data-nav-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    setVars();
  }
  toggle.addEventListener('click', () => {
    if (DESKTOP.matches) {
      setNavOpen(!root.hasAttribute('data-nav-open'));
    } else if (drawer && !drawer.open) {
      closeSearch();
      drawer.showModal();
      toggle.setAttribute('aria-expanded', 'true');
    }
  });

  if (drawer) {
    drawer.addEventListener('close', () => toggle.setAttribute('aria-expanded', 'false'));
    drawer.querySelector('[data-nnh-drawer-close]').addEventListener('click', () => drawer.close());
    drawer.addEventListener('click', (e) => {
      if (e.target !== drawer) return; // backdrop clicks land on the dialog box itself
      const r = drawer.getBoundingClientRect();
      if (e.clientX > r.right || e.clientX < r.left || e.clientY < r.top || e.clientY > r.bottom) drawer.close();
    });
    drawer.querySelectorAll('[data-nnh-acc]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.nnh-dnav__item');
        const open = !item.classList.contains('is-open');
        item.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }

  DESKTOP.addEventListener('change', () => {
    if (DESKTOP.matches && drawer && drawer.open) drawer.close();
    closeSearch();
    update();
  });

  /* ---------- desktop dropdowns on touch screens: first tap opens, second tap follows the link ---------- */
  root.querySelectorAll('[data-nnh-navitem] > .nnh-nav__link').forEach((link) => {
    link.addEventListener('click', (e) => {
      if (!window.matchMedia('(hover: none)').matches) return;
      const item = link.parentElement;
      if (item.hasAttribute('data-open')) return;
      e.preventDefault();
      root.querySelectorAll('[data-nnh-navitem][data-open]').forEach((el) => el.removeAttribute('data-open'));
      item.setAttribute('data-open', '');
    });
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-nnh-navitem]')) {
      root.querySelectorAll('[data-nnh-navitem][data-open]').forEach((el) => el.removeAttribute('data-open'));
    }
  });

  /* ---------- search ---------- */
  let controller = null;
  let timer = 0;
  let lastQuery = '';
  const openFly = () => search.classList.add('is-open');
  function closeSearch() {
    search.classList.remove('is-open');
    root.removeAttribute('data-search-open');
  }
  const setMode = (mode) => fly.setAttribute('data-mode', mode);

  async function runQuery(q) {
    if (controller) controller.abort();
    controller = new AbortController();
    setMode('loading');
    const url = new URL(root.dataset.suggestUrl, window.location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('section_id', RESULTS_SECTION);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sr = doc.querySelector('[data-nnh-sr]');
      if (q !== lastQuery) return;
      results.replaceChildren(...(sr ? sr.childNodes : []));
      setMode('results');
    } catch (err) {
      if (err.name === 'AbortError') return;
      results.replaceChildren();
      setMode('quick');
    }
  }

  input.addEventListener('focus', openFly);
  input.addEventListener('input', () => {
    const q = input.value.trim();
    lastQuery = q;
    openFly();
    clearTimeout(timer);
    if (!q) {
      if (controller) controller.abort();
      setMode('quick');
      return;
    }
    setMode('loading');
    timer = setTimeout(() => runQuery(q), 220);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      input.blur();
    }
  });
  document.addEventListener('pointerdown', (e) => {
    if (search.classList.contains('is-open') && !search.contains(e.target) && !e.target.closest('[data-nnh-search-open]')) {
      if (root.hasAttribute('data-search-open')) return; // mobile takeover closes via Cancel only
      search.classList.remove('is-open');
    }
  });
  root.querySelector('[data-nnh-search-open]').addEventListener('click', () => {
    root.setAttribute('data-search-open', '');
    openFly();
    input.focus();
  });
  root.querySelector('[data-nnh-search-close]').addEventListener('click', () => {
    input.value = '';
    lastQuery = '';
    setMode('quick');
    closeSearch();
  });

  update();
}

const root = document.querySelector('[data-nnh]');
if (root && !root.hasAttribute('data-nnh-ready')) {
  root.setAttribute('data-nnh-ready', '');
  initHeader(root);
}
